import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from 'src/shared/api/api';

const BASE_URL = '/ads-account/api/v1/activation/';
const REDUCER_PATH = 'ActivationApi';

export interface MarketplaceCountry {
  country: string;
  countryName: string;
}

export interface BillingState {
  isoCode: string;
  name: string;
}

export interface BillingCountry {
  isoCode: string;
  name: string;
  states: BillingState[];
}

export interface AgreementDocument {
  languageCode: string;
  languageDisplayText: string;
  document: string;
}

export interface RequestPageContext {
  managerAccountId: string;
  managerAccountName: string;
  marketplaceCountries: MarketplaceCountry[];
  billingCountries: BillingCountry[];
  timeZoneOptions: string[];
  industryOptions: string[];
  adspAgreementDocuments: AgreementDocument[];
  adspRegion: string;
  locale: string;
  obfuscatedMarketplaceId: string;
  principalObfuscatedId: string;
  redirectUrl?: string;
}

export interface ActivatePageContext {
  managerAccountId: string;
  managerAccountName: string;
  marketplaceCountries: MarketplaceCountry[];
  billingCountries: BillingCountry[];
  timeZoneOptions: string[];
  adspRegion: string;
  locale: string;
  obfuscatedMarketplaceId: string;
  principalObfuscatedId: string;
  isActivated: boolean;
  shadowEntityId: string;
  shadowEntityIdList: string[];
  shadowEntityUpdated: boolean;
  sellerOfRecords: Record<string, string>;
  multiMarketPlaceCountries: MarketplaceCountry[];
  managerAccountType: string;
  initialData: Record<string, unknown>;
  amazonUsername: string;
  redirectUrl?: string;
}

export interface SubmitActivationRequestBody {
  principalObfuscatedId: string;
  managerAccountId: string;
  companyProfile: {
    industry: string;
    partnerId?: string;
    foundingDate: string;
    estimatedMonthlySpend: number;
    size: number;
  };
  activationRequest: {
    marketplace: { countryIsoCode: string; multiMarketplace: boolean };
    companyName: string;
    address: {
      line1: string;
      line2?: string;
      postalCode: string;
      city: string;
      countryIsoCode: string;
      state?: string;
      stateIsoCode?: string;
    };
    contact: {
      name: string;
      emails: string;
      phoneNumber: string;
      website: string;
      businessRegistrationNumber?: string;
    };
    billingDetails: {
      mandatoryPurchaseOrderNumber: boolean;
      taxInformation: Record<string, unknown>;
    };
    settings: {
      timeZone: string;
      currency?: string;
      requirePoNumber: boolean;
    };
  };
}

export interface SubmitActivateBody {
  principalObfuscatedId: string;
  managerAccountId: string;
  amazonUsername: string;
  activationRequest: {
    marketplace: { countryIsoCode?: string; multiMarketplace: boolean };
    companyName: string;
    address: {
      line1: string;
      line2?: string;
      postalCode: string;
      city: string;
      state: string;
      countryIsoCode: string;
      stateIsoCode?: string;
    };
    contact: {
      name: string;
      emails: string;
      phoneNumber: string;
      website?: string;
      businessRegistrationNumber?: string;
    };
    billingDetails: {
      mandatoryPurchaseOrderNumber: boolean;
      taxInformation: Record<string, unknown>;
    };
    settings: {
      timeZone: string;
      currency?: string;
      globalSellerOfRecord?: string;
      amznAdSalesPartner?: boolean;
      productType?: string;
    };
  };
  activationTerms: {
    paymentTerms: string; // DAYS_30 | DAYS_45 | DAYS_60
  };
}

export interface SubmitRequestResponse {
  successUrl: string;
}

export interface SubmitActivateResponse {
  successUrl: string;
  shadowEntityId: string;
}

export interface SubmitActivateErrorResponse {
  errorMessage: string;
  shadowEntityUpdated?: boolean;
}

export const activationApi = createApi({
  reducerPath: REDUCER_PATH,
  baseQuery: axiosBaseQuery({ baseUrl: BASE_URL }),
  endpoints: (builder) => ({
    getRequestContext: builder.query<RequestPageContext, { entityId: string }>({
      query: ({ entityId }) => ({
        url: `request-context?entityId=${encodeURIComponent(entityId)}`,
        method: 'GET',
      }),
    }),
    submitActivationRequest: builder.mutation<
      SubmitRequestResponse,
      { entityId: string; body: SubmitActivationRequestBody }
    >({
      query: ({ entityId, body }) => ({
        url: `request?entityId=${encodeURIComponent(entityId)}`,
        method: 'POST',
        data: body,
      }),
    }),
    getActivateContext: builder.query<
      ActivatePageContext,
      { managerAccountId: string; regionMatched: boolean }
    >({
      query: ({ managerAccountId, regionMatched }) => ({
        url: `activate-context?managerAccountId=${managerAccountId}&regionMatched=${regionMatched}`,
        method: 'GET',
      }),
    }),
    submitActivation: builder.mutation<
      SubmitActivateResponse,
      { managerAccountId: string; body: SubmitActivateBody }
    >({
      query: ({ managerAccountId, body }) => ({
        url: `activate?managerAccountId=${managerAccountId}`,
        method: 'POST',
        data: body,
      }),
    }),
  }),
});

export const {
  useGetRequestContextQuery,
  useSubmitActivationRequestMutation,
  useGetActivateContextQuery,
  useSubmitActivationMutation,
} = activationApi;
